/**
 * Template Upload Component
 * Upload single sample statement for template learning
 */

import React, { useState, useRef } from 'react';
import { templatesAPI } from '../../services/api';

interface TemplateUploadProps {
  onUploadSuccess?: (templateId: string) => void;
}

export const TemplateUpload: React.FC<TemplateUploadProps> = ({ onUploadSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [bankName, setBankName] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file
      if (!['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(selectedFile.type)) {
        setError('Please select a PDF or image file (PNG, JPEG)');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must not exceed 10MB');
        return;
      }

      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    if (!bankName.trim()) {
      setError('Please enter a bank name');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setProgress('Uploading file...');

      // Step 1: Upload the sample file
      const uploadResponse = await templatesAPI.uploadSample(file, bankName.trim());

      setProgress('Processing with AI...');

      // Step 2: Process the sample to create template
      const processResponse = await templatesAPI.processSample(uploadResponse.sampleId);

      setSuccess(true);
      setProgress('Template created successfully!');
      setFile(null);
      setBankName('');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (onUploadSuccess && processResponse.template?.id) {
        onUploadSuccess(processResponse.template.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
      setProgress('');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      // Simulate file input change
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(droppedFile);
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        handleFileChange({ target: { files: dataTransfer.files } } as any);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Upload Sample Statement
        </h2>

        <p className="text-gray-600 mb-6">
          Upload a sample bank statement (PDF or image) to automatically create a template
          using AI-powered layout analysis.
        </p>

        {/* Bank Name Input */}
        <div className="mb-6">
          <label htmlFor="bank-name" className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name *
          </label>
          <input
            id="bank-name"
            type="text"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="e.g., Chase, Bank of America, Wells Fargo"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={uploading}
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter the name of the bank for this template
          </p>
        </div>

        {/* Drag and Drop Area */}
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
          />

          <label
            htmlFor="file-upload"
            className="cursor-pointer block"
          >
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <p className="mt-2 text-sm text-gray-600">
              <span className="text-blue-600 hover:text-blue-700 font-medium">
                Click to upload
              </span>{' '}
              or drag and drop
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, PNG, JPG up to 10MB
            </p>
          </label>
        </div>

        {/* Selected File */}
        {file && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="text-red-600 hover:text-red-800"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Progress */}
        {progress && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-800 text-sm">{progress}</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <p className="text-green-800 text-sm">
              Template created successfully! You can now use it to generate statements.
            </p>
          </div>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={!file || !bankName.trim() || uploading}
          className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
            !file || !bankName.trim() || uploading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {uploading ? 'Processing...' : 'Upload and Create Template'}
        </button>
      </div>
    </div>
  );
};
